import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AdditionalPayment, BookingModel, Pax } from 'ts/package/interfaces/BookPackageResponse';
import { useTranslation } from 'react-i18next';
import FormGroupSelect from 'components/ui/FormGroupSelect/FormGroupSelect';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import FormGroupDatepicker from 'components/ui/FormGroupDatepicker/FormGroupDatepicker';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { addYears, formatPrice, formatPriceCurrency, getCurrency, showError } from 'utils/utils';
import {
  PassengerType,
  getGender,
  getPackagePassengerValidation,
} from 'constants/package';
import { validate } from 'utils/validation';
import { sendRequest } from 'services/package';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import BookPayment from 'components/shared/BookPayment';
import { CmsPageResponse } from 'ts/shared/interfaces/CmsPageResponse';
import { Credit2000PaymentInfo } from 'ts/shared/interfaces/Generic';
import GeneralConditionsModal from 'components/shared/GeneralConditionsModal';
import OrderTripCompany from 'components/shared/OrderTripCompany';
import PhoneInput from 'components/shared/ui/PhoneInput';
import ProposalHeart from 'components/shared/Proposal/Create/ProposalHeart';
import { dateFormat } from 'utils/shared/utils';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import i18n, { getFlight } from 'utils/package';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSuitcase } from '@fortawesome/free-solid-svg-icons';
import { MainContext } from 'contexts/MainContext';
import { isNetravel, isTT } from 'constants/generic';

interface OrderBookProps {
  bookingModel: BookingModel;
  onClickBook: () => void;
  couponAmount: string;
  netravelPage?: CmsPageResponse;
  onChangeFacility: (payments: AdditionalPayment[]) => void;
  additionalPaymentsSelected: AdditionalPayment[];
}

const OrderBook: FC<OrderBookProps> = ({
  bookingModel: { paxes, ...bookingModel },
  additionalPaymentsSelected,
  ...props
}) => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isTour = useRef(pathname.includes('tour')).current;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [paxesState, setPaxesState] = useState(paxes);
  const flightDate = getFlight(bookingModel?.details.options)?.outbound.date || new Date();
  const [isConditionsOpen, setIsConditionsOpen] = useState(false);
  const [companyId, setCompanyId] = useState('');
  const [tripId, setTripId] = useState('0');
  const [urlParams] = useSearchParams();
  const { proposal, setProposal } = useContext(MainContext);
  const [additionalPayments] = useState<AdditionalPayment[]>(
    bookingModel.roomAdditionalPayments.filter(
      (pay, i, arr) =>
        arr.findIndex((p) => p.value === pay.value && p.paxCode === pay.paxCode) === i
    )
  );
  useEffect(() => {
    let newPaxesState = paxesState.map((pax) => ({ ...pax, gender: pax.gender || 'M' }));

    let currentPaxIndex = 0;
    bookingModel.rooms.forEach((room, index) => {
      let infantsUrl = urlParams.getAll('selectedInfantInRooms');
      const infantsInRoom = infantsUrl.filter((infant) =>
        infant.includes(room.bookCode + '-')
      )?.[0];

      const infantsInRoomCount = infantsInRoom ? +infantsInRoom.split('-')[1] : 0;
      infantsUrl = infantsUrl.filter((infant) => !infant.includes(infantsInRoom || ''));
      for (let i = 0; i < room.complect.length + infantsInRoomCount; i++) {
        if (newPaxesState[currentPaxIndex]) {
          newPaxesState[currentPaxIndex].roomIndex = index;
        }
        currentPaxIndex++;
      }
    });
    setPaxesState(newPaxesState);
  }, []); // eslint-disable-line

  const onClickSend = async () => {
    setIsSubmitted(true);

    if (isValid()) {
      setIsLoading(true);
      const flightOptIndex = +(urlParams.get('flightOptIndex') || '0');
      const flightIndex = +(urlParams.get('flightIndex') || '0');
      const newFlights = bookingModel.details.flights?.length
        ? [bookingModel.details.flights[flightIndex]]
        : bookingModel.details.flights;
      const newOptions = bookingModel.details.options?.[0].flights?.length
        ? [
            {
              ...bookingModel.details.options[0],
              flights: [bookingModel.details.options?.[0].flights[flightOptIndex]],
            },
          ]
        : bookingModel.details.options;

      const body = {
        ...bookingModel,
        details: {
          ...bookingModel.details,
          multiPackage: {},
          flights: newFlights,
          options: newOptions,
        },
        paxes: paxesState.map((pax) => ({
          ...pax,
          dob: pax.dob ? dateFormat(pax.dob, 'yyyy-MM-dd') : null,
        })),
        credit2000PaymentInfo: bookingModel.credit2000PaymentInfo,
        paymentSettings: bookingModel.credit2000PaymentInfo,
        roomAdditionalPaymentsSelected: additionalPaymentsSelected,
        companyId,
        tripId,
        couponAmount: parseInt(props.couponAmount || '0'),
        rooms: bookingModel.rooms.map((room, index) => ({
          ...room,
          catalogAdditionalPayments: additionalPaymentsSelected.filter(
            (pay) => pay.roomIndex === index
          ),
        })),
      };

      await props.onClickBook();

      const response = await sendRequest(body);
      if (response) {
        if (response.sessionExpired) {
          showError(t('home.session_expired'));
        } else if (response.hasErrors) {
          console.log('ILPackages/BookComplete ' + response.errorMessage);
          showError(t('something_went_wrong'));
        } else {
          sessionStorage.removeItem('screenshotsPackages');
          sessionStorage.setItem('packageSuccessOrder', JSON.stringify(response.data));
          const params = isTour
            ? ''
            : `?flightOptIndex=${flightOptIndex}&flightIndex=${flightIndex}`;
          navigate(`/${isTour ? 'tour' : 'ilpackage'}/success${params}`);
        }
      }

      setIsLoading(false);
    }
  };
  const onClickSubmit = async (credit2000: Credit2000PaymentInfo) => {
    setIsSubmitted(true);

    if (isValid()) {
      setIsLoading(true);
      const flightOptIndex = +(urlParams.get('flightOptIndex') || '0');
      const flightIndex = +(urlParams.get('flightIndex') || '0');
      const newFlights = bookingModel.details.flights?.length
        ? [bookingModel.details.flights[flightIndex]]
        : bookingModel.details.flights;
      const newOptions = bookingModel.details.options?.[0].flights?.length
        ? [
            {
              ...bookingModel.details.options[0],
              flights: [bookingModel.details.options?.[0].flights[flightOptIndex]],
            },
          ]
        : bookingModel.details.options;

      const body = {
        ...bookingModel,
        details: {
          ...bookingModel.details,
          multiPackage: {},
          flights: newFlights,
          options: newOptions,
        },
        paxes: paxesState.map((pax) => ({
          ...pax,
          dob: pax.dob ? dateFormat(pax.dob, 'yyyy-MM-dd') : null,
        })),
        creditCard: true,
        credit2000PaymentInfo: credit2000,
        paymentSettings: credit2000,
        roomAdditionalPaymentsSelected: additionalPaymentsSelected,
        tripId,
        companyId,
        couponAmount: parseInt(props.couponAmount || '0'),
        rooms: bookingModel.rooms.map((room, index) => ({
          ...room,
          catalogAdditionalPayments: additionalPaymentsSelected.filter(
            (pay) => pay.roomIndex === index
          ),
        })),
      };
      await props.onClickBook();

      const response = await sendRequest(body);
      if (response) {
        if (response.sessionExpired) {
          showError(t('home.session_expired'));
        } else if (response.hasErrors) {
          console.log('ILPackages/BookComplete ' + response.errorMessage);
          showError(t('something_went_wrong'));
        } else {
          sessionStorage.removeItem('screenshotsPackages');
          sessionStorage.setItem('packagePaymentOrder', JSON.stringify(response));
          const params = isTour
            ? ''
            : `?flightOptIndex=${flightOptIndex}&flightIndex=${flightIndex}`;
          navigate(`/${isTour ? 'tour' : 'ilpackage'}/payment${params}`);
        }
      }

      setIsLoading(false);
    }
  };

  const isValid = () => {
    let result = true;
    paxesState.forEach((pax, index) => {
      const validation = getPackagePassengerValidation(index);
      if (!validate(validation, pax)) {
        result = false;
      }
    });
    return result && termsAccepted;
  };

  const onChangeField = (pax: Pax, roomIndex: number, index: number) => {
    const newPaxesState = [...paxesState];
    const firstPaxIndex = paxesState.findIndex((pax) => pax.roomIndex === roomIndex);

    const paxIndex = firstPaxIndex + index;
    newPaxesState[paxIndex] = pax;
    setPaxesState(newPaxesState);
  };

  const isChild = (pax: Pax) =>
    [PassengerType.Child, PassengerType.Infant].includes(pax.paxTypeCode as PassengerType);

  const onClickBaggage = (
    payment: AdditionalPayment,
    roomIndex: number,
    index: number,
    checkboxType?: string
  ) => {
    const firstPaxIndex = paxesState.findIndex((pax) => pax.roomIndex === roomIndex);
    const paxIndex = firstPaxIndex + index + 1;
    const payIndex = additionalPaymentsSelected.findIndex(
      (pay) =>
        pay.value === payment.value && pay.paxCode === paxIndex && pay.checkboxType === checkboxType
    );

    const legId = checkboxType === 'inbound' ? 2 : payment.legId;

    const newPayments =
      payIndex === -1
        ? [...additionalPaymentsSelected, { ...payment, checkboxType, legId, roomIndex }]
        : additionalPaymentsSelected.filter((_, i) => i !== payIndex);

    props.onChangeFacility(newPayments);
  };

  const onClickProposal = () => {
    if (!proposal || !setProposal) return;

    const existingProposalIndex = proposal.ilPackages.findIndex(
      (proposal) => proposal.item.deal.dealId === bookingModel.details.deal.dealId
    );

    if (existingProposalIndex !== -1) {
      const updatedPackages = proposal.ilPackages.filter(
        (_, index) => index !== existingProposalIndex
      );
      setProposal({ ...proposal, ilPackages: updatedPackages });
    } else {
      const addPrice = additionalPaymentsSelected.reduce(
        (acc, payment) => acc + payment.addPrice,
        0
      );
      const newProposal = {
        item: JSON.parse(JSON.stringify(bookingModel.details)),
        currency: bookingModel.details.deal.currency,
        price: bookingModel.totalPrice + addPrice,
        displayPrice: true,
        searchToken: urlParams.get('searchToken') || '',
        selectedRooms: urlParams.getAll('selectedRooms'),
        selectedInfantInRooms: urlParams.getAll('selectedInfantInRooms'),
        type: urlParams.get('type') || '',
        rooms: bookingModel.rooms.map((room, index) => ({
          ...room,
          catalogAdditionalPayments: additionalPaymentsSelected.filter(
            (pay) => pay.roomIndex === index
          ),
        })),
      };
      setProposal({ ...proposal, ilPackages: [...proposal.ilPackages, newProposal] });
    }
  };

  const getPaymentDesc = (payment: AdditionalPayment) => {
    if (i18n.language === 'he') {
      return payment.descLocal || payment.value;
    }
    return payment.value || payment.descLocal;
  };
  return (
    <div className="details book">
      <div className="book-form default-shadow">
        <div className="title-underline text-lg title-header-grey mb-0">
          {t('passenger.details')}
        </div>
        {!!props.netravelPage?.page?.languages?.[0]?.title?.body && (
          <div className="netravel-book-1">
            <div className="text-contrast text-bold">
              {props.netravelPage.page.languages[0].title.desc}
            </div>
            <div
              className="text-st"
              dangerouslySetInnerHTML={{ __html: props.netravelPage.page.languages[0].title.body }}
            />
          </div>
        )}
        <div className="color-danger mb-10 text-xsm mt-5">{t('only_english_input')}</div>
        {bookingModel.rooms.map((room, roomIndex) => (
          <div key={roomIndex} className="mb-20">
            <div className="text-cap text-contrast text-bold text-md mb-10">
              <span className="mr-5 ml-5">
                {room.roomType?.toLowerCase()} ({formatPrice(room.salePrice, room.currency)})
              </span>

              <span className="ticket-badge text-st">{roomIndex + 1}</span>
            </div>
            <div className="ph-10">
              {paxesState
                .filter((pax) => pax.roomIndex === roomIndex)
                .map((pax, index) => (
                  <div className="book-form-room" key={index}>
                    <div className="title text-st mb-15">
                      <span className="mr-5">
                        {t('passenger.guest')} {index + 1}
                      </span>
                    </div>
                    <div className="form-wrapper row">
                      <FormGroupSelect
                        onChange={(gender) =>
                          onChangeField({ ...pax, gender: gender }, roomIndex, index)
                        }
                        value={isChild(pax) ? 'C' : pax.gender}
                        label={t('passenger.gender')}
                        className="col-md-4 col-12"
                        options={getGender(pax.paxTypeCode as PassengerType)}
                        disabled={isChild(pax)}
                        needToTranslate={true}
                      />
                      <FormGroup
                        name="firstName"
                        onChange={(firstName) =>
                          onChangeField({ ...pax, firstName }, roomIndex, index)
                        }
                        value={pax.firstName}
                        label={t('passenger.fn')}
                        className="col-md-4 col-12"
                        validation={getPackagePassengerValidation(index).firstName}
                        isSubmitted={isSubmitted}
                      />
                      <FormGroup
                        name="lastName"
                        onChange={(lastName) =>
                          onChangeField({ ...pax, lastName }, roomIndex, index)
                        }
                        value={pax.lastName}
                        label={t('passenger.ln')}
                        className="col-md-4 col-12"
                        validation={getPackagePassengerValidation(index).lastName}
                        isSubmitted={isSubmitted}
                      />
                      {isChild(pax) && (
                        <FormGroupDatepicker
                          className="col-md-4 col-12 form-group-calendar-first"
                          value={pax.dob}
                          label={t('passenger.dob')}
                          onChange={(dob) => onChangeField({ ...pax, dob }, roomIndex, index)}
                          minDate={addYears(
                            pax.paxTypeCode === PassengerType.Infant ? -2 : -16,
                            new Date(flightDate)
                          )}
                          maxDate={addYears(
                            pax.paxTypeCode === PassengerType.Infant ? 0 : -2,
                            new Date(flightDate)
                          )}
                          scrollOnFocusMobile={true}
                        />
                      )}
                    </div>
                    <div className="form-wrapper row">
                      {index === 0 && roomIndex === 0 && (
                        <Fragment>
                          <PhoneInput
                            onChange={(phone) => onChangeField({ ...pax, phone }, roomIndex, index)}
                            value={pax.phone}
                            isSubmitted={isSubmitted}
                            validation={getPackagePassengerValidation(index).phone}
                            className="col-md-8 col-12"
                          />
                          <FormGroup
                            name="email"
                            type="email"
                            onChange={(email) => onChangeField({ ...pax, email }, roomIndex, index)}
                            value={pax.email}
                            label={t('passenger.email')}
                            className="col-md-4 col-12"
                            validation={getPackagePassengerValidation(index).email}
                            isSubmitted={isSubmitted}
                          />
                        </Fragment>
                      )}
                    </div>
                    {additionalPayments
                      .filter(
                        (pay) =>
                          pay.paxCode - 1 ===
                          paxesState.findIndex((pax) => pax.roomIndex === roomIndex) + index
                      )
                      .map((payment, paymentIndex) => (
                        <div className="mb-20 text-lh flex-wrap display-flex" key={paymentIndex}>
                          {!isTT && !isNetravel && (
                            <Tooltip content={`[${payment.rule}] [${payment.restType}]`}>
                              <FontAwesomeIcon
                                icon={faSuitcase}
                                className="mr-10 text-contrast rtl-imp"
                              />
                            </Tooltip>
                          )}

                          {(isTT || isNetravel) && (
                            <FontAwesomeIcon
                              icon={faSuitcase}
                              className="mr-10 text-contrast rtl-imp"
                            />
                          )}

                          <span className="mr-10">{getPaymentDesc(payment)}</span>
                          {!!payment.addPrice && (
                            <Fragment>
                              <span className="text-bold mr-10 text-contrast rtl-imp">
                                ({formatPriceCurrency(payment.addPrice, getCurrency(room.currency))}
                                )
                              </span>
                              {(payment.displayType || payment.restType) === 'PT' && (
                                <Checkbox
                                  label={t('package.per_trip')}
                                  onChange={() =>
                                    onClickBaggage(payment, roomIndex, index, payment.checkboxType)
                                  }
                                />
                              )}

                              {['OWDEP', 'PL', 'PL1'].includes(
                                payment.displayType || payment.restType
                              ) && (
                                <Checkbox
                                  label={t('flight.outbound')}
                                  onChange={() =>
                                    onClickBaggage(payment, roomIndex, index, 'outbound')
                                  }
                                />
                              )}

                              {['OWRET', 'PL', 'PL2'].includes(
                                payment.displayType || payment.restType
                              ) && (
                                <Checkbox
                                  label={t('flight.inbound')}
                                  onChange={() =>
                                    onClickBaggage(payment, roomIndex, index, 'inbound')
                                  }
                                />
                              )}

                              {!['PT', 'PL', 'OWDEP', 'OWRET', 'PL1', 'PL2'].includes(
                                payment.displayType || payment.restType
                              ) && <span>{payment.displayType || payment.restType}</span>}
                            </Fragment>
                          )}
                        </div>
                      ))}
                  </div>
                ))}
            </div>
          </div>
        ))}
        
          <OrderTripCompany
            companyId={companyId}
            tripId={tripId}
            onChangeCompanyId={setCompanyId}
            onChangeTripId={setTripId}
          />
        
        <div className="checkbox-inline display-flex mb-30">
          <Checkbox
            checked={termsAccepted}
            onChange={setTermsAccepted}
            label={''}
            hasError={isSubmitted && !termsAccepted}
            blicked={true}
            htmlLabel={
              <Fragment>
                <span className="mr-10">{t('passenger.confirm')}</span>
                <span
                  className="btn-lnk"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsConditionsOpen(true);
                  }}
                >
                  {t('passenger.read_more')}
                </span>
              </Fragment>
            }
          />
        </div>
        {isConditionsOpen && (
          <GeneralConditionsModal product="LDS" onClose={() => setIsConditionsOpen(false)} />
        )}
        {!!props.netravelPage?.page?.languages?.[0]?.title_2?.body && (
          <div className="netravel-book-2">
            <div className="text-contrast text-bold">
              {props.netravelPage.page.languages[0].title_2.desc}
            </div>
            <div
              className="text-st"
              dangerouslySetInnerHTML={{
                __html: props.netravelPage.page.languages[0].title_2.body,
              }}
            />
          </div>
        )}
        
          <BookPayment
            onClickSend={onClickSend}
            onClickSubmit={onClickSubmit}
            disabled={isLoading}
            privileges={bookingModel.privilegesInfo}
            credit2000={bookingModel.credit2000PaymentInfo}
            isFormValid={isValid()}
            systemProducts="Nofshon"
          />
        
      </div>
      {createPortal(
        <ProposalHeart
          onClickPropsal={onClickProposal}
          active={
            !!proposal?.ilPackages.some(
              (proposal) => proposal.item.deal.dealId === bookingModel.details.deal.dealId
            )
          }
        />,
        document.querySelector('.flight-details-summary .title') as Element
      )}
    </div>
  );
};

export default withErrorBoundary(OrderBook, 'OrderBook');
